import { useEffect, useMemo, useState } from "react";
import { X, Star } from "lucide-react";
import { api } from "@/lib/api";
import ReviewCard from "@/components/common/ReviewCard";

/**
 * PropertyReviewsModal — per-property reviews dialog.
 *
 * Triggered from any "see reviews" affordance on PropertyDetail (stats tile
 * links, "X reviews" count, "See all reviews" in the reviews section). Fetches
 * only the reviews for this property directly so the count + average always
 * reflects the live state — no parent-component prop drilling means every new
 * review added in admin shows up the next time the modal opens.
 *
 * Filter: star rating (5 / 4+ / 3+ / all). Source filter intentionally kept
 * out to keep the property-level UX focused — guests don't care which platform
 * the review came from at that level of detail.
 */
export default function PropertyReviewsModal({ open, onClose, property, initialStar = "all" }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [star, setStar] = useState(initialStar);

  useEffect(() => {
    if (!open || !property?.slug) return;
    setStar(initialStar);
    setLoading(true);
    api
      .get("/reviews", { params: { property_slug: property.slug } })
      .then(({ data }) => setReviews(data))
      .finally(() => setLoading(false));
  }, [open, property?.slug, initialStar]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      if (star === "all") return true;
      const n = Number(r.rating || 0);
      if (star === "5") return n >= 4.95;
      if (star === "4") return n >= 4;
      if (star === "3") return n >= 3;
      return true;
    });
  }, [reviews, star]);

  const avg = reviews.length
    ? reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / reviews.length
    : 0;
  const fiveStar = reviews.filter((r) => Number(r.rating || 0) >= 4.95).length;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="property-reviews-modal"
      className="fixed inset-0 z-[110] flex items-stretch justify-center bg-noir/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-none bg-parchment shadow-soft sm:h-[86vh] sm:rounded-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-forest/10 bg-parchment/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-amber">Reviews · {property?.display_name}</p>
              <p className="mt-1 flex items-center gap-2 font-display text-xl text-forest sm:text-2xl">
                <Star size={18} className="fill-gold text-gold" />
                {avg.toFixed(2)}
                <span className="text-sm font-normal text-noir/70">· {reviews.length} reviews · {fiveStar} five-star</span>
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="close-property-reviews"
              aria-label="Close reviews"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-forest/20 bg-white text-forest hover:bg-mist"
            >
              <X size={18} />
            </button>
          </div>

          {/* Filter chips */}
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { value: "all", label: "All" },
              { value: "5", label: "5★ only" },
              { value: "4", label: "4★ and above" },
              { value: "3", label: "3★ and above" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStar(opt.value)}
                data-testid={`prop-reviews-filter-${opt.value}`}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  star === opt.value
                    ? "border-forest bg-forest text-parchment"
                    : "border-forest/20 bg-white text-forest hover:bg-mist/50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {loading ? (
            <p className="py-12 text-center text-sm text-noir/60">Loading reviews…</p>
          ) : filtered.length === 0 ? (
            <p className="py-12 text-center text-sm text-noir/60">No reviews match this filter yet.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((r) => (
                <ReviewCard key={r.id} r={r} clamp={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
